// HelpTopicDetail — /help/:topic (and /help/:topic/step/:stepIndex).
//
// Fetches the full topic, renders a stepper rail + step panel. Step
// completion persists to localStorage keyed by slug. The stepIndex URL
// segment is deep-link shareable (spec §route).

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Clock,
  ShieldAlert,
} from 'lucide-react'

import { getTopic } from './api'
import type { Topic, TopicEnvelope } from './types'
import {
  categoryLabel,
  readCompletedSteps,
  writeCompletedSteps,
} from './topic-utils'
import { ScreenshotWithCallouts } from './ScreenshotWithCallouts'
import { Callout } from './Callout'
import { DeepLinkButton } from './DeepLinkButton'
import { AskButlerChip } from './AskButlerChip'
import { WasThisHelpfulPrompt } from './WasThisHelpfulPrompt'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; env: TopicEnvelope }
  | { kind: 'disabled' }
  | { kind: 'missing' }
  | { kind: 'error'; message: string }

export function HelpTopicDetail() {
  const { topic: slug, stepIndex: stepParam } = useParams()
  const navigate = useNavigate()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })
  const [completed, setCompleted] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!slug) {
      setState({ kind: 'missing' })
      return
    }
    setCompleted(readCompletedSteps(slug))
    let cancelled = false
    setState({ kind: 'loading' })
    getTopic(slug)
      .then((env) => {
        if (cancelled) return
        setState({ kind: 'ready', env })
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return
        if (err.status === 404) {
          setState({ kind: 'missing' })
        } else if (err.status === undefined) {
          setState({ kind: 'error', message: err.message })
        } else {
          setState({ kind: 'error', message: err.message })
        }
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const topic = state.kind === 'ready' ? state.env.topic : null
  const isStale = state.kind === 'ready' ? state.env.is_stale : false
  const daysSince = state.kind === 'ready' ? state.env.days_since_verified : null

  // Clamp the URL step param to a valid index. Invalid/out-of-range
  // params silently land on step 0 rather than 404-ing; step deep-links
  // are meant to be share-friendly, not strict.
  const activeIndex = useMemo(() => {
    if (!topic) return 0
    const raw = stepParam ? Number(stepParam) : 0
    if (!Number.isFinite(raw)) return 0
    return Math.min(Math.max(0, Math.floor(raw)), topic.steps.length - 1)
  }, [topic, stepParam])

  const activeStep = topic ? topic.steps[activeIndex] : null

  const goStep = useCallback(
    (i: number) => {
      if (!topic || !slug) return
      const clamped = Math.min(Math.max(0, i), topic.steps.length - 1)
      navigate(`/help/${encodeURIComponent(slug)}/step/${clamped}`)
    },
    [navigate, slug, topic],
  )

  const toggleComplete = useCallback(
    (stepId: string) => {
      if (!slug) return
      setCompleted((prev) => {
        const next = new Set(prev)
        if (next.has(stepId)) next.delete(stepId)
        else next.add(stepId)
        writeCompletedSteps(slug, next)
        return next
      })
    },
    [slug],
  )

  if (state.kind === 'loading') {
    return <TopicSkeleton />
  }
  if (state.kind === 'disabled') {
    return (
      <NotFoundPanel
        title="Help is turned off"
        body="The help site is disabled by feature flag. Ask your assistant in chat for now."
      />
    )
  }
  if (state.kind === 'missing') {
    return (
      <NotFoundPanel
        title="We don't have a walkthrough for that yet"
        body="Head back to the help index, or ask your assistant to walk you through it in chat."
      />
    )
  }
  if (state.kind === 'error') {
    return (
      <NotFoundPanel
        title="Couldn't load that walkthrough"
        body={state.message}
        tone="error"
      />
    )
  }
  if (!topic || !activeStep) return null

  return (
    <div
      data-testid="help-topic-detail"
      data-topic-slug={topic.slug}
      className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8"
    >
      <Link
        to="/help"
        className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-soft transition hover:text-butler-600 dark:text-slate-400 dark:hover:text-butler-200"
      >
        <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
        All help topics
      </Link>

      <TopicHeader topic={topic} />

      {isStale && daysSince != null && (
        <div
          role="note"
          data-testid="help-stale-banner"
          className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-100"
        >
          <ShieldAlert aria-hidden="true" className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Last verified {daysSince} days ago. If screenshots don't match the current
            portal, tell your assistant.
          </span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        <StepperRail
          topic={topic}
          activeIndex={activeIndex}
          completed={completed}
          onSelect={goStep}
        />
        <StepPanel
          topic={topic}
          stepIndex={activeIndex}
          completed={completed.has(activeStep.id)}
          onToggleComplete={() => toggleComplete(activeStep.id)}
          onPrev={() => goStep(activeIndex - 1)}
          onNext={() => goStep(activeIndex + 1)}
        />
      </div>

      <footer className="flex flex-col gap-4 border-t border-hairline pt-6 dark:border-slate-700">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-ink-soft dark:text-slate-400">
            Prefer a hand? your assistant can do any of these steps for you.
          </p>
          <AskButlerChip seed={topic.chat_seed_prompt} variant="footer" />
        </div>
        <WasThisHelpfulPrompt topic={topic.slug} stepId={activeStep.id} />
      </footer>
    </div>
  )
}

function TopicHeader({ topic }: { topic: Topic }) {
  return (
    <header>
      <span className="inline-flex items-center rounded-full bg-butler-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-butler-600 dark:bg-butler-500/15 dark:text-butler-300">
        {categoryLabel(topic.category)}
      </span>
      <h1 className="mt-2 text-2xl font-display font-semibold text-ink dark:text-slate-100 sm:text-3xl">
        {topic.title}
      </h1>
      <p className="mt-1.5 max-w-3xl text-sm text-ink-soft dark:text-slate-400">
        {topic.summary}
      </p>
      <div className="mt-3 flex items-center gap-4 text-[11px] text-ink-soft dark:text-slate-500">
        <span className="inline-flex items-center gap-1">
          <Clock aria-hidden="true" className="h-3 w-3" />
          {topic.est_total_minutes} min
        </span>
        <span>Last verified {topic.last_verified_at}</span>
        {topic.bl181_version && (
          <span className="hidden sm:inline">Against {topic.bl181_version}</span>
        )}
      </div>
    </header>
  )
}

function StepperRail({
  topic,
  activeIndex,
  completed,
  onSelect,
}: {
  topic: Topic
  activeIndex: number
  completed: Set<string>
  onSelect: (i: number) => void
}) {
  return (
    <nav
      aria-label="Walkthrough steps"
      data-testid="help-stepper-rail"
      className="rounded-xl border border-hairline bg-surface-raised p-3 dark:border-slate-700 dark:bg-slate-900 lg:sticky lg:top-6 lg:self-start"
    >
      <ol className="flex flex-col gap-1">
        {topic.steps.map((s, i) => {
          const done = completed.has(s.id)
          const active = i === activeIndex
          const Icon = done ? CheckCircle2 : Circle
          return (
            <li key={s.id}>
              <button
                type="button"
                data-testid={`help-step-link-${i}`}
                aria-current={active ? 'step' : undefined}
                onClick={() => onSelect(i)}
                className={`flex w-full items-start gap-2 rounded-md px-2.5 py-2 text-left text-sm transition ${
                  active
                    ? 'bg-butler-500/15 text-butler-700 dark:bg-butler-500/20 dark:text-butler-100'
                    : 'text-ink hover:bg-surface-page dark:text-slate-200 dark:hover:bg-slate-800'
                }`}
              >
                <Icon
                  aria-hidden="true"
                  className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                    done
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : active
                        ? 'text-butler-600 dark:text-butler-300'
                        : 'text-ink-soft dark:text-slate-500'
                  }`}
                />
                <span className="flex-1">
                  <span className="block text-[11px] font-medium uppercase tracking-[0.08em] text-ink-soft dark:text-slate-500">
                    Step {i + 1}
                  </span>
                  <span className="block font-medium leading-snug">{s.title}</span>
                </span>
              </button>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

function StepPanel({
  topic,
  stepIndex,
  completed,
  onToggleComplete,
  onPrev,
  onNext,
}: {
  topic: Topic
  stepIndex: number
  completed: boolean
  onToggleComplete: () => void
  onPrev: () => void
  onNext: () => void
}) {
  const step = topic.steps[stepIndex]
  const isFirst = stepIndex === 0
  const isLast = stepIndex === topic.steps.length - 1

  return (
    <article
      data-testid="help-step-panel"
      data-step-id={step.id}
      className="flex flex-col gap-5 rounded-xl border border-hairline bg-surface-raised p-5 dark:border-slate-700 dark:bg-slate-900"
    >
      <div>
        <div className="flex items-center justify-between gap-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-butler-600 dark:text-butler-300">
            Step {stepIndex + 1} of {topic.steps.length}
          </div>
          {step.est_minutes != null && (
            <div className="text-[11px] text-ink-soft dark:text-slate-500">
              ~{step.est_minutes} min
            </div>
          )}
        </div>
        <h2 className="mt-1 text-xl font-semibold text-ink dark:text-slate-100">
          {step.title}
        </h2>
        <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink dark:text-slate-200">
          {step.narrative}
        </p>
      </div>

      {step.screenshot && (
        <ScreenshotWithCallouts screenshot={step.screenshot} stepIndex={stepIndex} />
      )}

      {step.callout && <Callout callout={step.callout} />}

      <div className="flex flex-wrap items-center gap-3">
        {step.deep_link && <DeepLinkButton link={step.deep_link} />}
        {step.chat_prompt && step.chat_prompt.trim() !== '' && (
          <AskButlerChip seed={step.chat_prompt} variant="inline" />
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-hairline pt-4 dark:border-slate-700">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink dark:text-slate-200">
          <input
            type="checkbox"
            checked={completed}
            onChange={onToggleComplete}
            data-testid="help-step-complete-toggle"
            className="h-4 w-4 rounded border-hairline text-butler-500 focus:ring-butler-500"
          />
          Mark step complete
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPrev}
            disabled={isFirst}
            data-testid="help-step-prev"
            className="rounded-md border border-hairline px-3 py-1.5 text-sm text-ink transition hover:border-butler-500 hover:text-butler-600 disabled:opacity-40 dark:border-slate-700 dark:text-slate-200"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={isLast}
            data-testid="help-step-next"
            className="rounded-md bg-butler-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-butler-600 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>
    </article>
  )
}

function TopicSkeleton() {
  return (
    <div
      aria-hidden="true"
      data-testid="help-topic-skeleton"
      className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8"
    >
      <div className="h-6 w-48 animate-pulse rounded bg-hairline dark:bg-slate-800" />
      <div className="h-8 w-96 animate-pulse rounded bg-hairline dark:bg-slate-800" />
      <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
        <div className="h-72 animate-pulse rounded-xl border border-hairline bg-surface-raised dark:border-slate-700 dark:bg-slate-900" />
        <div className="h-72 animate-pulse rounded-xl border border-hairline bg-surface-raised dark:border-slate-700 dark:bg-slate-900" />
      </div>
    </div>
  )
}

function NotFoundPanel({
  title,
  body,
  tone = 'info',
}: {
  title: string
  body: string
  tone?: 'info' | 'error'
}) {
  const toneClass =
    tone === 'error'
      ? 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-100'
      : 'border-hairline bg-surface-raised text-ink dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
  return (
    <div className="mx-auto mt-20 max-w-lg px-4">
      <div
        role="status"
        data-testid="help-notfound-panel"
        className={`rounded-xl border px-5 py-6 ${toneClass}`}
      >
        <p className="text-sm font-semibold">{title}</p>
        <p className="mt-1 text-sm opacity-80">{body}</p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Link
            to="/help"
            className="inline-flex items-center gap-1 rounded-md border border-butler-500/40 bg-butler-500/10 px-3 py-1.5 text-sm font-medium text-butler-600 hover:bg-butler-500/20 dark:border-butler-300/40 dark:text-butler-200"
          >
            Help index
          </Link>
          <AskButlerChip
            seed="I got stuck on a help page — can you walk me through what I was trying to do?"
            variant="inline"
            label="Ask in chat"
          />
        </div>
      </div>
    </div>
  )
}
